import React from 'react'
import { Stack, Divider, useTheme } from '@mui/material'
import {
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic
} from '@mui/icons-material'
import xss from 'xss'

interface DummyEditorProps {
  /**
   * Raw HTML content to display in the editor.
   * Sanitized using DomPurify and displayed as read-only rich content.
   */
  htmlContent: string
  /**
   * If enabled, a greyed out toolbar is displayed at the top of the editor.
   */
  showToolbar?: boolean
}

/**
 * A dummy editor component that displays read-only rich content in a div when
 * given raw HTML (don't worry - we sanitize it first).
 */
export const DummyEditor: React.FC<DummyEditorProps> = (props) => {
  const { htmlContent: _dangerousHtmlContent, showToolbar } = props
  const theme = useTheme()

  const sanitizedHtmlContent = xss(_dangerousHtmlContent)

  const editorToolbar = (
    <Stack direction="row" spacing={2} color={theme.palette.action.disabled}>
      <FormatBold />
      <FormatItalic />
      <FormatAlignLeft />
      <FormatAlignCenter />
      <FormatAlignRight />
      <FormatAlignJustify />
    </Stack>
  )

  // Note: We're not currently using a real editor component but instead
  //  mocking it with a div in the interest of time.
  return (
    <Stack
      spacing={2}
      p={2}
      border="1px solid rgba(0, 0, 0, 0.1)"
      borderRadius={1}
      style={{ backgroundColor: 'white' }}
    >
      {showToolbar && (
        <>
          {editorToolbar}
          <Divider variant="middle" />
        </>
      )}
      <div
        dangerouslySetInnerHTML={{
          // Use this prop with great caution. Any value passed here should
          //  be sanitized first.
          // See https://stackoverflow.com/questions/37337289/react-js-set-innerhtml-vs-dangerouslysetinnerhtml
          __html: sanitizedHtmlContent
        }}
      />
    </Stack>
  )
}
